import { textStylePreset, captionTextStylePreset, textStyleDefaults, captionTextY, resolveTextStyle } from 'vibe-remotion'
import type { EditSpecDict, EditSpecOverlayDict, EditSpecTextPlacementDict, EditSpecTextStyleDict } from '../../../../generated/nodeDataTypes'
import { editCaptionText, moveCaption, trimCaption, deleteCaption } from './captionMutations'

export type TextDocument = Pick<EditSpecDict, 'overlays' | 'captions'>

/** A view of existing records, never a second persisted list. */
export interface TextTrackItem extends EditSpecTextPlacementDict {
  key: string
  id: string
  source: 'overlay' | 'segment'
  text: string
  start_seconds: number
  duration_seconds: number
  animation?: EditSpecOverlayDict['animation']
  style_preset?: EditSpecOverlayDict['style_preset']
}
export type TextPatch = Partial<Omit<TextTrackItem, 'id' | 'key' | 'source'>>
export const TEXT_LANE_HEIGHT = 44

export function textTrackItems(spec: TextDocument): TextTrackItem[] {
  return [
    ...(spec.overlays ?? []).map((item): TextTrackItem => ({ ...item, key: `overlay:${item.id}`, source: 'overlay' })),
    ...(spec.captions?.segments ?? []).map((item): TextTrackItem => ({ ...item,
      x: item.x ?? 50, y: item.y ?? captionTextY(spec.captions?.style_preset),
      key: `segment:${item.id}`, source: 'segment', duration_seconds: item.end_seconds - item.start_seconds,
    })),
  ]
}

export function textTrackLanes(items: TextTrackItem[]): TextTrackItem[][] {
  const lanes: TextTrackItem[][] = [[]]
  for (const item of [...items].sort((a, b) => (b.layer ?? 0) - (a.layer ?? 0) || a.start_seconds - b.start_seconds || a.key.localeCompare(b.key))) {
    const lane = lanes.find((lane) => lane.every((other) => other.start_seconds + other.duration_seconds <= item.start_seconds || item.start_seconds + item.duration_seconds <= other.start_seconds))
    if (lane) lane.push(item)
    else lanes.push([item])
  }
  return lanes
}

export function updateText<T extends TextDocument>(spec: T, key: string, patch: TextPatch): T {
  const item = textTrackItems(spec).find((item) => item.key === key)
  if (!item) throw new Error(`Unknown Text item ${key}`)
  if (patch.text !== undefined && !patch.text.trim()) throw new Error('Text cannot be empty')
  const start = patch.start_seconds ?? item.start_seconds
  const duration = patch.duration_seconds ?? item.duration_seconds
  if (!Number.isFinite(start) || !Number.isFinite(duration) || start < 0 || duration <= 0) throw new Error('Text must have a non-negative start and positive duration')
  if (item.source === 'overlay') return { ...spec, overlays: spec.overlays?.map((overlay) => overlay.id === item.id ? { ...overlay, ...patch } : overlay) }
  let next = spec
  if (patch.text !== undefined && patch.text !== item.text) next = editCaptionText(next, item.id, patch.text)
  if (patch.start_seconds !== undefined && patch.duration_seconds === undefined) next = moveCaption(next, item.id, start)
  if (patch.duration_seconds !== undefined) next = trimCaption(next, item.id, start, start + duration)
  const { text: _text, start_seconds: _start, duration_seconds: _duration, animation: _animation, style_preset: _preset, ...placement } = patch
  if (!next.captions) throw new Error('Text segment container disappeared')
  return { ...next, captions: { ...next.captions, segments: next.captions.segments.map((segment) => segment.id === item.id ? { ...segment, ...placement } : segment) } }
}

/** An in-flight preview gesture (drag, resize, rotate) on one of the two Text documents the editor can show. */
export interface TextDraft {
  source: 'editspec' | 'legacy'
  key: string
  patch: TextPatch
  revision: number
}

/**
 * Preview-only projection of a draft onto the document being rendered. Runs during render, so it must
 * never throw: a draft that belongs to the other document, or whose item disappeared mid-gesture, is
 * not applied. The commit path still goes through updateText and reports real validation errors.
 */
export function applyTextDraft<T extends TextDocument>(spec: T, draft: TextDraft | null, source: TextDraft['source']): T {
  if (!draft || draft.source !== source) return spec
  if (!textTrackItems(spec).some((item) => item.key === draft.key)) return spec
  return updateText(spec, draft.key, draft.patch)
}

export function addText(spec: EditSpecDict, id: string, start: number, text: string, cta?: boolean): EditSpecDict
export function addText<T extends TextDocument>(spec: T, id: string, start: number, text: string, cta?: boolean): T
export function addText<T extends TextDocument>(spec: T, id: string, start: number, text: string, cta = false): T {
  if (spec.overlays?.some((item) => item.id === id)) throw new Error(`Duplicate Text id ${id}`)
  const item: EditSpecOverlayDict = { id, kind: 'text', text, start_seconds: start, duration_seconds: 3,
    x: 50, y: cta ? 78 : 50, anchor: 'center', animation: 'none', style_preset: cta ? 'pill_pop' : 'subtitle',
    style: { font_family: 'sans', font_size: cta ? 48 : 64, bold: true, text_align: 'center', color: '#FFFFFF', outline: 'none', shadow: 'soft',
      ...(cta ? { background_color: '#C4622A', background_opacity: 1, padding: 20, border_radius: 32, shadow: 'none' } : {}),
    },
  }
  return { ...spec, overlays: [...(spec.overlays ?? []), item] }
}

export function deleteText<T extends TextDocument>(spec: T, key: string): T {
  const item = textTrackItems(spec).find((item) => item.key === key)
  if (!item) throw new Error(`Unknown Text item ${key}`)
  return item.source === 'segment' ? deleteCaption(spec, item.id) : { ...spec, overlays: spec.overlays?.filter((overlay) => overlay.id !== item.id) }
}

export function duplicateText<T extends TextDocument>(spec: T, key: string, id: string): { spec: T; key: string } {
  const item = textTrackItems(spec).find((item) => item.key === key)
  if (!item) throw new Error(`Unknown Text item ${key}`)
  if (textTrackItems(spec).some((item) => item.id === id)) throw new Error(`Duplicate Text id ${id}`)
  if (item.source === 'overlay') {
    const overlay = spec.overlays!.find((overlay) => overlay.id === item.id)!
    return { spec: { ...spec, overlays: [...spec.overlays!, { ...overlay, id, x: Math.min(100, overlay.x + 3), y: Math.min(100, overlay.y + 3), layer: (overlay.layer ?? 0) + 1 }] }, key: `overlay:${id}` }
  }
  const segment = spec.captions!.segments.find((segment) => segment.id === item.id)!
  return { spec: { ...spec, captions: { ...spec.captions!, segments: [...spec.captions!.segments, { ...segment, id, layer: (segment.layer ?? 0) + 1 }] } }, key: `segment:${id}` }
}

export function textInspectorDefaults(spec: TextDocument, item: TextTrackItem): EditSpecTextStyleDict {
  const css = item.source === 'segment'
    ? captionTextStylePreset(spec.captions?.style_preset, spec.captions?.font_scale)
    : textStylePreset(item.style_preset ?? 'bold_caption');
  return textStyleDefaults({ ...css, ...resolveTextStyle(item.style, css) });
}
